import React from "react"
import { Helmet } from "react-helmet"

const SVTogetherPP = () => {
  return (
    <div style={{
      maxWidth: '800px',
      margin: '50px auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      <Helmet>
        <title>Privacy Policy - SourceView Together</title>
        <meta name="description" content="Privacy Policy for SourceView Together Bible reading application" />
      </Helmet>

      <h1 style={{
        textAlign: 'center',
        color: '#2c3e50',
        borderBottom: '3px solid #3498db',
        paddingBottom: '10px',
        marginBottom: '30px'
      }}>
        Privacy Policy for SourceView Together
      </h1>

      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <p><strong>Effective Date:</strong> December 2024</p>
        <p><strong>Last Updated:</strong> November 2025</p>
      </div>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #3498db', paddingLeft: '15px' }}>
          Overview
        </h2>
        <p>
          SourceView Together is a Bible reading application designed for individual and group study.
          We are committed to protecting your privacy and being transparent about our data practices.
          This Privacy Policy explains how we handle information when you use our app.
        </p>
        <p style={{ marginTop: '15px', fontWeight: 'bold' }}>
          <strong>Important:</strong> All your personal reading data (progress, notes, emojis) is stored locally on your device
          and is never uploaded to our servers or shared with third parties, regardless of any analytics settings.
        </p>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #16a085', paddingLeft: '15px' }}>
          Anonymous Analytics Data (Optional)
        </h2>
        <p style={{ marginBottom: '10px' }}>
          <strong>Updated November 2025:</strong> To improve the app and understand which features are most valuable,
          we offer an optional anonymous analytics feature that collects usage data through PostHog <strong>only with your explicit consent</strong>.
        </p>
        <p style={{ marginBottom: '15px', fontStyle: 'italic' }}>
          By default, analytics are disabled. You must opt-in to enable this feature.
        </p>

        <h3 style={{ color: '#16a085', marginTop: '20px' }}>What We Collect (With Your Permission):</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#27ae60', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✅</span>
            Feature usage (which features are used, how often)
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#27ae60', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✅</span>
            Reading plan popularity (anonymized counts)
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#27ae60', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✅</span>
            Session duration and frequency
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#27ae60', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✅</span>
            App version and device type (for compatibility)
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#27ae60', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✅</span>
            Error and crash reports (to fix bugs)
          </li>
        </ul>

        <h3 style={{ color: '#16a085', marginTop: '20px' }}>What We Do NOT Collect:</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your name, email, or any personal information
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your reading content or specific Bible passages read
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your device identifier or advertising ID (IDFA/AAID)
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your location
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your reading progress details
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>❌</span>
            Your notes or emoji reactions content
          </li>
        </ul>

        <h3 style={{ color: '#16a085', marginTop: '20px' }}>Your Control:</h3>
        <ul>
          <li><strong>Analytics is opt-in</strong> - You must give consent before any data is collected</li>
          <li><strong>Easy opt-out</strong> - You can disable analytics anytime in Settings</li>
          <li><strong>No impact on app</strong> - Opting out does not affect any app functionality</li>
          <li><strong>Local data stays local</strong> - All your reading progress, emojis, and notes remain private on your device regardless of analytics setting</li>
        </ul>

        <h3 style={{ color: '#16a085', marginTop: '20px' }}>Third-Party Service</h3>
        <p>
          We use PostHog, a privacy-focused analytics platform, to collect and analyze anonymous usage data.
          PostHog does not receive any personally identifiable information. Data is retained for 90 days, then deleted.
          Learn more: <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>https://posthog.com/privacy</a>
        </p>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #27ae60', paddingLeft: '15px' }}>
          Information Stored Locally on Your Device
        </h2>
        <p>The following data is stored <strong>only</strong> on your device using a local SQLite database and <strong>never</strong> leaves your device:</p>

        <h3 style={{ color: '#27ae60', marginTop: '20px' }}>Reading Progress Data:</h3>
        <ul>
          <li>Completed Bible segments</li>
          <li>Started reading plans and challenges</li>
          <li>Reading streaks and achievements</li>
          <li>Emoji reactions you've added to passages</li>
          <li>Reading session history and statistics</li>
          <li>Book completion status</li>
        </ul>

        <h3 style={{ color: '#27ae60', marginTop: '20px' }}>App Preferences:</h3>
        <ul>
          <li>Selected Bible version (default: NLT)</li>
          <li>App language settings</li>
          <li>Current reading position</li>
          <li>Display preferences</li>
        </ul>

        <div style={{
          backgroundColor: '#d5f4e6',
          border: '2px solid #27ae60',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '20px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#27ae60' }}>
            ⚠️ Important: All this personal data remains on your device and is never uploaded to our servers or shared with third parties,
            regardless of whether you opt-in to anonymous analytics.
          </p>
        </div>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #e74c3c', paddingLeft: '15px' }}>
          Information We Do NOT Collect
        </h2>
        <p>We want to be clear about what we <strong>never</strong> collect, store, or transmit to external servers, regardless of analytics settings:</p>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Personal names or identifying information
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Email addresses or phone numbers
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            User accounts or login credentials
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Location data
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Device identifiers for tracking purposes (IDFA/AAID)
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Your reading content or specific Bible passages
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Your reading progress details
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Your notes or emoji reactions content
          </li>
          <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#e74c3c', fontSize: '20px', marginRight: '10px', fontWeight: 'bold' }}>✗</span>
            Any data that could personally identify you
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #f39c12', paddingLeft: '15px' }}>
          Permissions We Request
        </h2>

        <h3 style={{ color: '#f39c12' }}>Camera Permission:</h3>
        <ul>
          <li>Used for scanning QR codes to join group Bible reading sessions</li>
          <li>Only used when you actively choose to scan a QR code</li>
          <li>QR code data is processed temporarily and immediately discarded</li>
          <li>You can deny this permission and still use all individual reading features</li>
        </ul>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #9b59b6', paddingLeft: '15px' }}>
          Data Security
        </h2>
        <ul>
          <li>All personal reading data is stored using SQLite with industry-standard security practices</li>
          <li>Your data is protected by your device's built-in security features</li>
          <li>Your personal reading data (progress, notes, emojis) is never transmitted over the internet</li>
          <li>The only data transmitted over the internet includes:
            <ul style={{ marginTop: '10px' }}>
              <li>App updates and Bible content downloads (necessary for app functionality)</li>
              <li>Anonymous analytics data (only if you opt-in, sent to PostHog as described above)</li>
            </ul>
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #e67e22', paddingLeft: '15px' }}>
          Children's Privacy
        </h2>
        <p>Our app is designed to be safe for users of all ages, including children under 13:</p>
        <ul>
          <li>No personal information is collected from anyone, including children</li>
          <li>All personal reading data (progress, notes, emojis) remains on the user's device and is never uploaded</li>
          <li>Anonymous analytics are opt-in only and do not collect personal information</li>
          <li>No online interactions with strangers are possible within the app</li>
        </ul>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #34495e', paddingLeft: '15px' }}>
          Data Deletion
        </h2>
        <p>You can delete your data at any time using these methods:</p>
        <ol>
          <li><strong>Reset the app:</strong> Clear your progress via app settings</li>
          <li><strong>Uninstall the app:</strong> Removes all data from your device</li>
          <li><strong>Device factory reset:</strong> Removes all app data</li>
        </ol>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #1abc9c', paddingLeft: '15px' }}>
          Contact Information
        </h2>
        <p>If you have questions about this Privacy Policy, please contact us:</p>
        <p><strong>Email:</strong> <a href="mailto:sourceviewbible@gmail.com" style={{ color: '#3498db' }}>sourceviewbible@gmail.com</a></p>
        <p><em>Suggested subject line: "Privacy Policy Question - SourceView Together"</em></p>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #95a5a6', paddingLeft: '15px' }}>
          Data Controller Information
        </h2>
        <ul>
          <li><strong>App Name:</strong> SourceView Together</li>
          <li><strong>Contact Email:</strong> sourceviewbible@gmail.com</li>
          <li><strong>Bundle ID:</strong> com.sourceview.together</li>
        </ul>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', borderLeft: '4px solid #8e44ad', paddingLeft: '15px' }}>
          Compliance
        </h2>
        <p>This Privacy Policy is designed to comply with:</p>
        <ul>
          <li>Apple App Store Review Guidelines</li>
          <li>Google Play Store Privacy Requirements</li>
          <li>General Data Protection Regulation (GDPR) principles</li>
          <li>California Consumer Privacy Act (CCPA) guidelines</li>
          <li>Children's Online Privacy Protection Act (COPPA)</li>
        </ul>
      </section>

      <section style={{
        marginTop: '40px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h2 style={{ color: '#2c3e50', textAlign: 'center', marginBottom: '15px' }}>
          Acknowledgement
        </h2>
        <p style={{ textAlign: 'center', margin: 0 }}>
          By using SourceView Together, you acknowledge that you have read and understood this Privacy Policy.
          This policy is effective from the date listed above and applies to all users of the application.
        </p>
      </section>
    </div>
  )
}

export default SVTogetherPP
